import { ScimGroups, Team } from '@run-diffusion/shared';

/**
 * One editable team assignment inside a group. Stored as a list while editing (Firestore holds an
 * unordered map) so a half-filled entry, a team with no role yet, can exist while the admin works.
 * `key` is a stable client id: display order is alphabetical by team name, so entries move as teams
 * are chosen and v-for needs something that does not shift with them.
 */
export type TeamMappingDraft = { key: number; teamId: string | null; teamUserRoleDefId: string | null };

/**
 * Client-only fields: _assignmentType drives the Scope select, _teamMappings holds the editable
 * team assignments. Both are hydrated from the doc and then user-controlled.
 */
export type ScimGroupRow = {
	id: string;
	companyUserRoleDefId?: string;
	_assignmentType?: 'team' | 'company';
	_teamMappings?: TeamMappingDraft[];
} & Partial<ScimGroups>;

/**
 * Comparable form of a row's assignment, used for the dirty check and for discard.
 *
 * `assignmentType` is stored rather than re-derived on discard. A group saved as company scope with
 * no role chosen yet has a null `companyUserRoleDefId`, which is indistinguishable from team scope
 * once the discriminator is gone, so discard used to silently rewrite its scope while the dirty
 * check went on reporting the row as saved.
 */
export type ScimAssignmentSnapshot = {
	assignmentType: 'team' | 'company';
	mappings: string;
	companyUserRoleDefId: string | null;
};

/** Canonical string for a set of team mappings: sorted, so key order never registers as a change. */
export function serializeTeamMappings (mappings: Record<string, string>): string {
	return Object.entries(mappings)
		.filter(([teamId, roleDefId]) => !!teamId && !!roleDefId)
		.map(([teamId, roleDefId]) => `${teamId}:${roleDefId}`)
		.sort()
		.join('|');
}

/**
 * Local edit wins, then the stored discriminator, then inference from the populated field for groups
 * the backfill has not reached yet.
 */
export function getRowAssignmentType (row: ScimGroupRow): 'team' | 'company' {
	if (row._assignmentType != null) return row._assignmentType;
	if (row.assignmentType === 'team' || row.assignmentType === 'company') return row.assignmentType;
	return row.companyUserRoleDefId ? 'company' : 'team';
}

/** Complete (team + role) pairs from a row's draft mappings, as the map Firestore stores. */
export function teamRoleMappingsForRow (row: ScimGroupRow): Record<string, string> {
	const out: Record<string, string> = {};
	(row._teamMappings ?? []).forEach((mapping: TeamMappingDraft) => {
		if (mapping.teamId && mapping.teamUserRoleDefId) out[mapping.teamId] = mapping.teamUserRoleDefId;
	});
	return out;
}

export function getScimGroupAssignmentSnapshot (row: ScimGroupRow): ScimAssignmentSnapshot {
	const assignmentType = getRowAssignmentType(row);
	const isCompany = assignmentType === 'company';
	return {
		assignmentType,
		mappings: isCompany ? '' : serializeTeamMappings(teamRoleMappingsForRow(row)),
		companyUserRoleDefId: isCompany ? (row.companyUserRoleDefId ?? null) : null,
	};
}

/** True when a row's current assignment differs from the snapshot it was last saved against. */
export function assignmentDiffersFromSnapshot (row: ScimGroupRow, saved: ScimAssignmentSnapshot): boolean {
	const current = getScimGroupAssignmentSnapshot(row);
	return current.assignmentType !== saved.assignmentType
		|| current.mappings !== saved.mappings
		|| current.companyUserRoleDefId !== saved.companyUserRoleDefId;
}

/** Rebuilds draft mapping rows from a snapshot's canonical string. `nextKey` supplies client ids. */
export function teamMappingsFromSnapshot (
	saved: ScimAssignmentSnapshot,
	nextKey: () => number,
): TeamMappingDraft[] {
	if (!saved.mappings) return [];
	return saved.mappings.split('|').map((pair: string) => {
		const [teamId, teamUserRoleDefId] = pair.split(':');
		return { key: nextKey(), teamId: teamId || null, teamUserRoleDefId: teamUserRoleDefId || null };
	});
}

/** Active teams A to Z by display name (same label as elsewhere in company UI). */
export function sortTeamsByName (teams: Team[]): Team[] {
	return [...teams].sort((a, b) =>
		(a.name || a.id || '').localeCompare(b.name || b.id || '', undefined, { sensitivity: 'base' }),
	);
}

/**
 * Draft mappings in display order: alphabetical by team name, with entries that have no team yet
 * pinned to the end. Order is derived rather than stored, so there is nothing for an admin to
 * arrange and the list reads the same on every machine.
 */
export function sortedTeamMappings (row: ScimGroupRow, teamsSorted: Team[]): TeamMappingDraft[] {
	const nameById = new Map<string, string>(teamsSorted.map((team: Team) => [team.id, team.name || '']));
	return [...(row._teamMappings ?? [])].sort((a, b) => {
		if (!a.teamId && !b.teamId) return a.key - b.key;
		if (!a.teamId) return 1;
		if (!b.teamId) return -1;
		return (nameById.get(a.teamId) || '').localeCompare(nameById.get(b.teamId) || '', undefined, { sensitivity: 'base' });
	});
}

/**
 * A group cannot map more teams than the company has, and a blank entry must be filled before
 * another is added, otherwise Add produces rows that can never resolve to a team.
 */
export function canAddTeamMapping (row: ScimGroupRow, teamsSorted: Team[]): boolean {
	const mappings = row._teamMappings ?? [];
	if (mappings.some((m: TeamMappingDraft) => !m.teamId)) return false;
	return mappings.length < teamsSorted.length;
}

/** Teams still selectable for a row: everything not already mapped, plus the entry's own team. */
export function availableTeamsForMapping (row: ScimGroupRow, mapping: TeamMappingDraft, teamsSorted: Team[]): Team[] {
	const taken = new Set(
		(row._teamMappings ?? [])
			.filter((m: TeamMappingDraft) => m.key !== mapping.key && !!m.teamId)
			.map((m: TeamMappingDraft) => m.teamId as string),
	);
	return teamsSorted.filter((team: Team) => !taken.has(team.id));
}

/** True when the row provisions nobody: no complete team mapping, or no company role. */
export function isRowUnmapped (row: ScimGroupRow): boolean {
	if (getRowAssignmentType(row) === 'company') return !row.companyUserRoleDefId;
	return Object.keys(teamRoleMappingsForRow(row)).length === 0;
}

/**
 * Teams that no group in the plan provisions. Deploying empties them, which is the single most
 * destructive thing this page can do, so the page shows them next to the groups rather than
 * describing the rule in a warning block.
 */
export function teamsWithNoGroupInPlan (rows: ScimGroupRow[], teamsSorted: Team[]): Team[] {
	const mapped = new Set<string>();
	rows.forEach((row: ScimGroupRow) => {
		if (getRowAssignmentType(row) !== 'team') return;
		Object.keys(teamRoleMappingsForRow(row)).forEach((teamId: string) => mapped.add(teamId));
	});
	return teamsSorted.filter((team: Team) => !mapped.has(team.id));
}

/**
 * Emails a company-scope group would give a role to that no team-scope group provisions.
 *
 * The sync blocks on these. An admin-created invite can carry a company role and grant it on
 * acceptance, but SCIM never does that, so a company-role member with no team assignment either holds
 * a role belonging to no team or is skipped outright. Predicted here from the same plan the sync
 * reads, so the page can say so on the row rather than letting the admin discover it from an
 * aborted-sync email.
 *
 * Rows missing the field their scope depends on provision nobody and are skipped, which matches the
 * sync's own filters: it selects team groups by a usable mapping and company groups by a chosen role.
 */
export function companyRoleEmailsWithoutTeamMapping (rows: ScimGroupRow[]): string[] {
	const teamMapped = new Set<string>();
	rows.forEach((row: ScimGroupRow) => {
		if (getRowAssignmentType(row) !== 'team') return;
		if (Object.keys(teamRoleMappingsForRow(row)).length === 0) return;
		getMemberEmailsList(row).forEach((email: string) => teamMapped.add(email));
	});

	const withoutTeam = new Set<string>();
	rows.forEach((row: ScimGroupRow) => {
		if (getRowAssignmentType(row) !== 'company' || !row.companyUserRoleDefId) return;
		getMemberEmailsList(row).forEach((email: string) => {
			if (!teamMapped.has(email)) withoutTeam.add(email);
		});
	});
	return Array.from(withoutTeam).sort((a, b) => a.localeCompare(b));
}

/** `memberEmails` as `{ [email]: true }` to an alphabetically sorted list for display. */
export function getMemberEmailsList (row: ScimGroupRow): string[] {
	const m = row.memberEmails;
	if (m == null || typeof m !== 'object' || Array.isArray(m)) return [];
	return Object.keys(m)
		.filter((k) => m[k] === true)
		.map((k) => k.toLowerCase().trim())
		.sort((a, b) => a.localeCompare(b));
}
